import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getSocket, ensureConnected } from '@/lib/socket';

const STORAGE_KEY = 'bot_command_state';

// Create the context
const AiCommandPaletteContext = createContext(null);

// Message types 
const MessageTypes = {
  USER: 'user',
  AI: 'ai'
};

// Context provider component
export const AiCommandPaletteProvider = ({ children, isOpen, onClose, setPageContext }) => {
  // Input and search state
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  
  // Default suggestions
  const [defaultSuggestions, setDefaultSuggestions] = useState([
    "What tasks need my attention?",
    "What should I work on next?",
    "Can you summarize my week?"
  ]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  
  // Context indicator state
  const [contextIndicator, setContextIndicator] = useState(null);
  
  // AI-related state
  const [aiResults, setAiResults] = useState(null);
  const [isAskMode, setIsAskMode] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Gathering context from your organizations');
  const [commandHistoryId, setCommandHistoryId] = useState(null);

  // Message state - simplified!
  const [completedMessages, setCompletedMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState({
    userQuery: '',
    aiBlocks: [],
    isStreaming: false,
    status: null
  });
  
  // Generic registries for AI-controlled placement
  const [registries, setRegistries] = useState({
    task_list: [],
    monitor_status: []
  });
  
  // Text buffer for placeholder detection
  const [textBuffer, setTextBuffer] = useState('');
  
  // UI state
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const isTransitioningToAiRef = useRef(false);
  const socketRef = useRef(null);
  
  // Hooks
  const { axiosInstance, user } = useAuth();
  const navigate = useNavigate();

  // Restore state from sessionStorage on mount
  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const state = JSON.parse(saved);
        if (state.commandHistoryId) setCommandHistoryId(state.commandHistoryId);
        if (state.isAskMode) setIsAskMode(state.isAskMode);
        if (state.completedMessages) setCompletedMessages(state.completedMessages);
        if (state.currentMessage) setCurrentMessage(state.currentMessage);
        if (state.registries) setRegistries(state.registries);
        console.log('[CommandPalette] Restored state from session:', state.commandHistoryId);
      } catch (e) {
        console.error('Failed to restore command state:', e);
      }
    }
  }, []); // Run once on mount

  // Generate context-specific suggestions
  const generateContextSuggestions = useCallback((context) => {
    if (!context) {
      return [
        "What needs my attention?",
        "Any overdue tasks?",
        "How was my week?"
      ];
    }

    switch (context.type) {
      case 'task':
        return [
          `Is ${context.label} done?`,
          `Summarize the activity on ${context.label}`,
          `Find tasks similar to ${context.label}`
        ];
      case 'project':
        return [
          `Show tasks in ${context.label}`,
          `Summarize the last week in ${context.label}`,
          `Anything waiting on me in ${context.label}?`
        ];
      case 'user':
        return [
          `What's assigned to ${context.label}?`,
          `Show completed tasks by ${context.label}`,
          `What's ${context.label} working on?`
        ];
      case 'organization':
        return [
          `Show active tasks in ${context.label}`,
          `Summarize the last week in ${context.label}`,
          `Anything waiting on me in ${context.label}?`
        ];
      case 'today':
        return [
          `What should I work on today?`,
          `What is due soon?`,
          `Anything waiting on me?`
        ];
      case 'monitor':
        return [
          `What's the status of this monitor?`,
          `Show recent run history`,
          `Are there any other monitors down?`
        ];
      case 'monitors':
        return [
          `Summarize all monitors`,
          `Which monitors are down?`,
          `Show all paused monitors`
        ];
      case 'projects':
        return [
          `Summarize all projects`,
          `Summarize tasks across all projects`,
          `Which projects have upcoming releases?`
        ];
      default:
        return [
          "What needs my attention?",
          "Any overdue tasks?",
          "How was my week?"
        ];
    }
  }, []);

  // Update suggestions when context changes
  useEffect(() => {
    const newSuggestions = generateContextSuggestions(contextIndicator);
    setDefaultSuggestions(newSuggestions);
  }, [contextIndicator, generateContextSuggestions]);

  // Reset state when closed (but keep conversation alive)
  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
      setSearchResults([]);
      setAiResults(null);
      setSelectedIndex(-1);
      setIsLoading(false);
      setLoadingMessage('Gathering context from your organizations');
      setError(null);
      // Keep isAskMode, completedMessages, currentMessage, commandHistoryId alive
      setShowSuggestions(true);
      // Don't reset context indicator when closing
    } else {
      // When opening, refresh suggestions based on current context
      const newSuggestions = generateContextSuggestions(contextIndicator);
      setDefaultSuggestions(newSuggestions);
      setShowSuggestions(true);
    }
  }, [isOpen, generateContextSuggestions, contextIndicator]);

  // Auto-focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle input change with context processing
  const handleInputChange = useCallback((value) => {
    setInputValue(value);
    // Hide suggestions when the user starts typing
    if (value.trim() !== '') {
      setShowSuggestions(false);
    } else {
      setShowSuggestions(true);
    }
  }, []);

  // Apply a suggestion to the input
  const applySuggestion = useCallback((suggestion) => {
    setInputValue(suggestion);
    setShowSuggestions(false);
    // Focus the input after applying a suggestion
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Helper to compare contexts and avoid unnecessary re-renders
  const areContextsEqual = useCallback((a, b) => {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.type !== b.type) return false;
    if (a.label !== b.label) return false;
    // Shallow compare data via JSON string for simplicity/safety here
    try {
      return JSON.stringify(a.data || {}) === JSON.stringify(b.data || {});
    } catch (_) {
      return false;
    }
  }, []);

  // Set context from current page/task (snapshot on open)
  const setContextFromPage = useCallback((context) => {
    const nextContext = context
      ? { type: context.type, label: context.label, data: context.data }
      : null;

    setContextIndicator((prev) => {
      if (areContextsEqual(prev, nextContext)) {
        return prev;
      }
      return nextContext;
    });
  }, [areContextsEqual]);

  // Remove the context indicator (palette-only clear; do not modify page context)
  const removeContext = useCallback(() => {
    setContextIndicator(null);
  }, []);

  // Search functionality
  const handleSearch = useCallback(async () => {
    if (!inputValue.trim()) return;
    
    try {
      const response = await axiosInstance.post('/ai-search/typeahead', {
        query: inputValue,
        context: contextIndicator
      });
      
      // Don't update results if we've already transitioned to AI mode
      if (!isAskMode) {
        setSearchResults(response.data.results);
        setAiResults(null);
        setError(null);
        
        // Only auto-select the AI option if there are no search results
        if (response.data.results.length === 0) {
          setSelectedIndex(0); // Select AI option
        } else {
          setSelectedIndex(-1); // No selection by default
        }
      }
    } catch (error) {
      console.error('Search error:', error);
      if (!isAskMode) {
        setError('An error occurred while searching. Please try again.');
        setSearchResults([]);
        setSelectedIndex(-1);
      }
    } finally {
      // Only clear loading state if we're not in AI mode or transitioning to it
      if (!isAskMode && !isTransitioningToAiRef.current) {
        setIsLoading(false);
      }
    }
  }, [inputValue, axiosInstance, contextIndicator, isAskMode]);


  // Initialize socket connection using shared socket manager
  useEffect(() => {
    const socket = getSocket();
    
    // Socket not ready yet - will be created when auth initializes
    // This prevents race condition where this component mounts before AuthContext's initSocket
    if (!socket) {
      console.log('[Command] Socket not ready yet, waiting for auth init');
      return;
    }
    
    socketRef.current = socket;

    const handleConnect = () => {
      console.log('[Command] Socket connected:', socket.id);
    };

    const handleDisconnect = () => {
      console.log('[Command] Socket disconnected');
    };

    // Listen for streaming content blocks with buffering
    const handleStream = (data) => {
      console.log('[Command] Stream data:', data.type);
      
      if (data.type === 'content_block' && data.block.type === 'text') {
        // Add to buffer
        setTextBuffer(prevBuffer => {
          const bufferedText = prevBuffer + data.block.content;
          
          // Look for incomplete placeholder at the end
          const openBraceIndex = bufferedText.lastIndexOf('{{');
          
          if (openBraceIndex !== -1) {
            const afterBraces = bufferedText.substring(openBraceIndex);
            const hasClosing = afterBraces.includes('}}');
            
            if (hasClosing || afterBraces.length >= 30) {
              // Complete placeholder or too long (not a placeholder) - render all
              setCurrentMessage(prev => {
                const blocks = [...prev.aiBlocks];
                const lastBlock = blocks[blocks.length - 1];
                
                if (lastBlock?.type === 'text') {
                  // Create new block object instead of mutating
                  blocks[blocks.length - 1] = {
                    ...lastBlock,
                    content: lastBlock.content + bufferedText
                  };
                } else {
                  blocks.push({ type: 'text', content: bufferedText });
                }
                
                return { ...prev, aiBlocks: blocks, isStreaming: true };
              });
              return ''; // Clear buffer
            } else {
              // Potentially incomplete placeholder - hold back
              const textToRender = bufferedText.substring(0, openBraceIndex);
              if (textToRender) {
                setCurrentMessage(prev => {
                  const blocks = [...prev.aiBlocks];
                  const lastBlock = blocks[blocks.length - 1];
                  
                  if (lastBlock?.type === 'text') {
                    // Create new block object instead of mutating
                    blocks[blocks.length - 1] = {
                      ...lastBlock,
                      content: lastBlock.content + textToRender
                    };
                  } else {
                    blocks.push({ type: 'text', content: textToRender });
                  }
                  
                  return { ...prev, aiBlocks: blocks, isStreaming: true };
                });
              }
              return afterBraces; // Keep only the {{ part buffered
            }
          } else {
            // No {{ found - safe to render
            setCurrentMessage(prev => {
              const blocks = [...prev.aiBlocks];
              const lastBlock = blocks[blocks.length - 1];
              
              if (lastBlock?.type === 'text') {
                // Create new block object instead of mutating
                blocks[blocks.length - 1] = {
                  ...lastBlock,
                  content: lastBlock.content + bufferedText
                };
              } else {
                blocks.push({ type: 'text', content: bufferedText });
              }
              
              return { ...prev, aiBlocks: blocks, isStreaming: true };
            });
            return ''; // Clear buffer
          }
        });
      }
    };

    const handleStatus = (data) => {
      console.log('[Command] Status:', data.message);
      setCurrentMessage(prev => ({
        ...prev,
        status: data.message,
        isStreaming: true
      }));
    };

    const handleBlockData = (data) => {
      console.log('[Command] Block data received:', data.blockType, data.blockId);
      setRegistries(prev => {
        const typeArray = [...(prev[data.blockType] || [])];
        
        // Ensure array is long enough for the blockId index
        while (typeArray.length <= data.blockId) {
          typeArray.push(null);
        }
        
        // Store at the correct blockId index
        typeArray[data.blockId] = data.data;
        
        return {
          ...prev,
          [data.blockType]: typeArray
        };
      });
    };

    const handleDone = (data) => {
      console.log('[Command] Stream complete');
      
      // Store commandHistoryId if provided
      if (data?.commandHistoryId) {
        console.log('[Command] Command History ID:', data.commandHistoryId);
        setCommandHistoryId(data.commandHistoryId);
      }
      
      // Flush any remaining buffered text
      setTextBuffer(buffer => {
        if (buffer) {
          setCurrentMessage(prev => {
            const blocks = [...prev.aiBlocks];
            const lastBlock = blocks[blocks.length - 1];
            
            if (lastBlock?.type === 'text') {
              // Create new block object instead of mutating
              blocks[blocks.length - 1] = {
                ...lastBlock,
                content: lastBlock.content + buffer
              };
            } else {
              blocks.push({ type: 'text', content: buffer });
            }
            
            return { ...prev, aiBlocks: blocks, isStreaming: false, status: null };
          });
        } else {
          // No buffer, just mark as done
          setCurrentMessage(prev => ({
            ...prev,
            isStreaming: false,
            status: null
          }));
        }
        return ''; // Clear buffer
      });
      
      setIsLoading(false);
      isTransitioningToAiRef.current = false;
      
      // Refocus input
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };

    const handleError = (data) => {
      console.error('[Command] Error:', data.message);
      setError(data.message || 'An error occurred while processing your request.');
      setIsLoading(false);
      setCurrentMessage({ userQuery: '', aiBlocks: [], isStreaming: false, status: null });
      setTextBuffer('');
      isTransitioningToAiRef.current = false;
    };

    // Attach listeners
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('command:stream', handleStream);
    socket.on('command:status', handleStatus);
    socket.on('command:block_data', handleBlockData);
    socket.on('command:done', handleDone);
    socket.on('command:error', handleError);

    // Cleanup on unmount (don't disconnect shared socket)
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('command:stream', handleStream);
      socket.off('command:status', handleStatus);
      socket.off('command:block_data', handleBlockData);
      socket.off('command:done', handleDone);
      socket.off('command:error', handleError);
    };
  }, []);

  // AI ask functionality with socket streaming
  const handleAiAsk = useCallback(async (providedQuery = null) => {
    // Use provided query if available, otherwise use input value
    const query = providedQuery || inputValue.trim();
    
    if (!query) return;

    if (!socketRef.current?.connected) {
      console.log('[Command] Socket not connected, attempting reconnection...');
      try {
        const connected = await ensureConnected();
        if (!connected) {
          setError('Connection not available. Please try again.');
          return;
        }
        // Update ref in case socket was recreated
        socketRef.current = getSocket();
      } catch (err) {
        console.error('[Command] Reconnection failed:', err);
        setError('Connection not available. Please try again.');
        return;
      }
    }
    
    // Mark that we're transitioning to AI mode
    isTransitioningToAiRef.current = true;
    
    // Cancel any pending search operations
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setSearchResults([]);
    
    // If there's a current message, move it to completed (capture registries first)
    setRegistries(currentRegistries => {
      setCurrentMessage(prev => {
        if (prev.userQuery) {
          // Save completed message with snapshot of current registries
          setCompletedMessages(msgs => [...msgs, {
            userQuery: prev.userQuery,
            aiBlocks: prev.aiBlocks,
            registries: { ...currentRegistries } // Snapshot!
          }]);
        }
        
        // Start new message with user query
        return {
          userQuery: query,
          aiBlocks: [],
          isStreaming: true,
          status: 'Thinking'
        };
      });
      
      // Return cleared registries for new query
      return {
        task_list: [],
        monitor_status: []
      };
    });
    
    setTextBuffer('');
    
    // Clear the input and set loading state
    setInputValue('');
    setIsLoading(true);
    
    // Set ask mode immediately when AI is triggered
    setIsAskMode(true);

    // Emit socket event to backend
    // Server loads conversation history from database using commandHistoryId
    // userId is now obtained from authenticated socket.user on the server
    console.log('[Command] Sending query via socket:', query, commandHistoryId ? `(continuing ${commandHistoryId})` : '(new conversation)');
    socketRef.current.emit('command:ask', {
      query: query,
      commandHistoryId: commandHistoryId,
      context: contextIndicator
    });

    }, [inputValue, user, contextIndicator, commandHistoryId]);

  // Debounced search effect
  useEffect(() => {
    // Skip if in ask mode or if input starts with /, we don't want to trigger search then
    if (isAskMode || inputValue.startsWith('/')) return;
    
    if (!inputValue.trim()) {
      setSearchResults([]);
      setSelectedIndex(-1);
      setIsLoading(false); // Reset loading state when input is cleared
      return;
    }

    setIsLoading(true);
    // No longer auto-selecting the AI option by default
    setSelectedIndex(-1);

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (inputValue && !inputValue.startsWith('/')) {
        handleSearch();
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
    };
  }, [inputValue, handleSearch, isAskMode]);

  // Keyboard navigation with context handling
  const handleKeyDown = useCallback((e) => {
    // Special handling for backspace to remove context
    if (e.key === 'Backspace' && contextIndicator && inputValue === '') {
      e.preventDefault();
      removeContext();
      return;
    }

    // Second backspace when context is already cleared and input is empty - act like Escape
    if (e.key === 'Backspace' && !contextIndicator && inputValue === '') {
      e.preventDefault();
      if (isAskMode) {
        // Clear persisted state
        sessionStorage.removeItem(STORAGE_KEY);
        
        setIsAskMode(false);
        setAiResults(null);
        setCompletedMessages([]);
        setCurrentMessage({ userQuery: '', aiBlocks: [], isStreaming: false, status: null });
        setCommandHistoryId(null);
        setInputValue('');
      } else {
        onClose();
      }
      return;
    }

    if (e.key === 'Escape') {
      if (isAskMode) {
        // Clear persisted state
        sessionStorage.removeItem(STORAGE_KEY);
        
        setIsAskMode(false);
        setAiResults(null);
        setCompletedMessages([]);
        setCurrentMessage({ userQuery: '', aiBlocks: [], isStreaming: false, status: null });
        setCommandHistoryId(null);
        setInputValue('');
      } else {
        onClose();
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      
      // Special handling for context commands that start with /
      if (inputValue.startsWith('/')) {
        // Extract the context value - everything after the slash up to the first space or end
        const contextValue = inputValue.includes(' ') 
          ? inputValue.substring(1, inputValue.indexOf(' ')).trim()
          : inputValue.substring(1).trim();

        // Make sure we have a valid context
        if (contextValue) {
          setContextIndicator({
            type: 'manual',
            value: contextValue,
            label: contextValue
          });
          
          // If there's text after the context, set it as input
          if (inputValue.includes(' ')) {
            setInputValue(inputValue.substring(inputValue.indexOf(' ')).trim());
          } else {
            setInputValue('');
          }
          return;
        }
      }
      
      if (isAskMode) {
        // In ask mode, hitting enter with text triggers a follow-up question
        if (inputValue.trim()) {
          handleAiAsk();
        }
      } else {
        // In search mode
        if (selectedIndex === -1) {
          // If nothing is selected but we have text, treat it as AI query
          if (inputValue.trim()) {
            handleAiAsk();
          }
        } else if (selectedIndex === searchResults.length) {
          // If the AI option is selected (now at the end), trigger AI response
          handleAiAsk();
        } else if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          // If a search result is selected, navigate to it
          const selectedTask = searchResults[selectedIndex];
          if (selectedTask.organization?.slug && selectedTask.key) {
            navigate(`/${selectedTask.organization.slug}/plan/${selectedTask.key}`);
            onClose();
          }
        }
      }
    } else if (inputValue.trim()) {
      const totalOptions = searchResults.length + 1; // +1 for AI option
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => {
            if (prev === -1) return 0; // First item if nothing selected
            return prev < totalOptions - 1 ? prev + 1 : prev;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => {
            if (prev === -1) return totalOptions - 1; // Last item if nothing selected
            return prev > 0 ? prev - 1 : prev;
          });
          break;
        case 'Tab':
          e.preventDefault();
          setSelectedIndex(prev => {
            if (prev === -1) {
              return e.shiftKey ? totalOptions - 1 : 0;
            }
            return e.shiftKey
              ? (prev > 0 ? prev - 1 : totalOptions - 1)
              : (prev < totalOptions - 1 ? prev + 1 : 0);
          });
          break;
      }
    }
  }, [isAskMode, inputValue, selectedIndex, searchResults, handleAiAsk, navigate, onClose, contextIndicator, removeContext]);

  // Status icon helper
  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'done':
        return 'done';
      case 'in_progress':
        return 'in_progress';
      default:
        return 'todo';
    }
  }, []);

  // Overlay click handler
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Cleanup when command palette closes
  useEffect(() => {
    if (!isOpen) {
      // Reset transitioning flag when closed
      isTransitioningToAiRef.current = false;
    }
  }, [isOpen]);

  // Save state to sessionStorage when it changes
  useEffect(() => {
    if (commandHistoryId || completedMessages.length > 0 || currentMessage.userQuery) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        commandHistoryId,
        isAskMode,
        completedMessages,
        currentMessage,
        registries
      }));
    }
  }, [commandHistoryId, isAskMode, completedMessages, currentMessage, registries]);

  // Loading message cycling effect
  useEffect(() => {
    if (!isLoading || !aiResults) return;

    const messages = [
      'Gathering context from your organizations',
      'Crunching data just for you',
      'Processing and generating a response'
    ];
    let index = 0;

    const interval = setInterval(() => {
      if (index < messages.length - 1) {
        index++;
        setLoadingMessage(messages[index]);
      }
    }, 3000);

    setLoadingMessage(messages[0]);

    return () => {
      clearInterval(interval);
      if (index < messages.length - 1) {
        const quickCycle = async () => {
          for (let i = index + 1; i < messages.length; i++) {
            setLoadingMessage(messages[i]);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        };
        quickCycle();
      }
    };
  }, [isLoading, aiResults]);

  // UI control callbacks
  const handleCloseKeyboardShortcuts = useCallback(() => {
    setShowKeyboardShortcuts(false);
  }, []);

  const handleBackToSearch = useCallback(() => {
    // Clear persisted state
    sessionStorage.removeItem(STORAGE_KEY);
    
    setIsAskMode(false);
    setAiResults(null);
    setInputValue('');
    // Clear messages
    setCompletedMessages([]);
    setCurrentMessage({ userQuery: '', aiBlocks: [], isStreaming: false, status: null });
    setCommandHistoryId(null);
    inputRef.current?.focus();
  }, []);

  // Load a previous command history
  const handleLoadHistory = useCallback((commandHistory) => {
    console.log('[CommandPalette] Loading history:', commandHistory);
    
    // Set command history ID
    setCommandHistoryId(commandHistory._id);
    
    // Switch to ask mode
    setIsAskMode(true);
    
    // Clear current state
    setInputValue('');
    
    // Process messages from the history
    const completedMsgs = [];
    
    // Track current user query and its associated data
    let currentUserQuery = null;
    let currentAiBlocks = [];
    let currentRegistries = {
      task_list: [],
      monitor_status: []
    };
    
    for (const message of commandHistory.messages) {
      if (message.role === 'user') {
        // If we have a previous user query, save it first
        if (currentUserQuery) {
          completedMsgs.push({
            userQuery: currentUserQuery,
            aiBlocks: currentAiBlocks,
            registries: currentRegistries
          });
        }
        
        // Start a new message pair
        currentUserQuery = message.content;
        currentAiBlocks = [];
        currentRegistries = {
          task_list: [],
          monitor_status: []
        };
      } else if (message.role === 'tool') {
        // Extract data from tool response for registries
        try {
          const data = JSON.parse(message.content);
          if (data.blockType && data.blockId !== undefined) {
            // Ensure array is long enough
            while (currentRegistries[data.blockType].length <= data.blockId) {
              currentRegistries[data.blockType].push(null);
            }
            // Store the data at the correct blockId
            currentRegistries[data.blockType][data.blockId] = {
              tasks: data.tasks,
              monitors: data.monitors,
              metadata: { total: data.total }
            };
          }
        } catch (e) {
          console.error('[CommandPalette] Failed to parse tool response:', e);
        }
      } else if (message.role === 'assistant' && message.content) {
        // Assistant text response (final response with placeholders)
        currentAiBlocks.push({
          type: 'text',
          content: message.content
        });
      }
      // Skip assistant messages with tool_calls (no content)
    }
    
    // Don't forget the last message pair
    if (currentUserQuery) {
      completedMsgs.push({
        userQuery: currentUserQuery,
        aiBlocks: currentAiBlocks,
        registries: currentRegistries
      });
    }
    
    // Set the restored state
    setCompletedMessages(completedMsgs);
    // Set registries to the last message's registries (or empty if no messages)
    setRegistries(completedMsgs.length > 0 ? { ...completedMsgs[completedMsgs.length - 1].registries } : {
      task_list: [],
      monitor_status: []
    });
    setCurrentMessage({ userQuery: '', aiBlocks: [], isStreaming: false, status: null });
    setIsLoading(false);
    
    console.log('[CommandPalette] Loaded', completedMsgs.length, 'message pairs');
  }, []);

  // Navigate to a task 
  const handleNavigateToTask = useCallback((task) => {
    if (task.organization?.slug && task.key) {
      navigate(`/${task.organization.slug}/plan/${task.key}`);
      onClose();
    }
  }, [navigate, onClose]);

  // Navigate to a monitor
  const handleNavigateToMonitor = useCallback((monitor) => {
    if (monitor.id) {
      navigate(`/monitors/${monitor.id}`);
      onClose();
    }
  }, [navigate, onClose]);

  // Handle link click
  const handleLinkClick = useCallback((url) => {
    if (url) {
      navigate(url);
      onClose();
    }
  }, [navigate, onClose]);

  // Context value
  const value = {
    // State
    inputValue,
    setInputValue,
    handleInputChange,
    isLoading,
    setIsLoading,
    searchResults,
    aiResults,
    isAskMode,
    selectedIndex,
    setSelectedIndex,
    loadingMessage,
    showKeyboardShortcuts,
    error,
    inputRef,
    completedMessages,
    currentMessage,
    contextIndicator,
    defaultSuggestions,
    showSuggestions,
    registries,
    
    // Actions
    handleSearch,
    handleAiAsk,
    handleKeyDown,
    getStatusIcon,
    handleOverlayClick,
    handleCloseKeyboardShortcuts,
    handleBackToSearch,
    handleLoadHistory,
    handleNavigateToTask,
    handleNavigateToMonitor,
    handleLinkClick,
    setContextFromPage,
    removeContext,
    applySuggestion,
    generateContextSuggestions,
    
    // Constants
    MessageTypes,
    
    // Control props
    isOpen,
    onClose
  };

  return (
    <AiCommandPaletteContext.Provider value={value}>
      {children}
    </AiCommandPaletteContext.Provider>
  );
};

// Custom hook to use the context
export const useAiCommandPalette = () => {
  const context = useContext(AiCommandPaletteContext);
  if (!context) {
    throw new Error('useAiCommandPalette must be used within an AiCommandPaletteProvider');
  }
  return context;
}; 